import { ChevronDown } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Popover } from './Popover.js';

export interface SelectOption {
  readonly value: string;
  readonly label: string;
  readonly disabled?: boolean;
}

export interface SelectProps {
  value: string;
  options: readonly SelectOption[];
  onChange: (value: string) => void;
  /**
   * What the trigger reads while `value` matches no option. An option that
   * means "no filter" is an option like any other: give it the empty value.
   */
  placeholder?: string;
  disabled?: boolean;
  /** The accessible name, where nothing visible beside the trigger names it. */
  label?: string;
  className?: string;
}

/** How long a run of letters counts as one word being typed at the list. */
const TYPE_AHEAD_MS = 700;

/**
 * The one select in the app. Focus never leaves the trigger: the list is
 * described to a screen reader through `aria-activedescendant`, which is what
 * lets Escape, Tab and type-ahead all be answered in one place.
 *
 * Glide's own overlay editors are not this — a cell editor lives and dies with
 * the overlay the grid opens, and has its own search box.
 */
export function Select(props: SelectProps): React.JSX.Element {
  const listId = useId();
  const [trigger, setTrigger] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const optionNodes = useRef<(HTMLLIElement | null)[]>([]);
  const typed = useRef({ text: '', at: 0 });

  const { options, value, onChange, disabled = false } = props;
  const selectedIndex = options.findIndex((option) => option.value === value);
  const selected = selectedIndex === -1 ? undefined : options[selectedIndex];

  const optionId = (index: number): string => `${listId}-option-${index}`;

  const step = useCallback(
    (from: number, delta: number): number => {
      const count = options.length;
      if (count === 0) return 0;
      let index = from;
      for (let tries = 0; tries < count; tries += 1) {
        index = (index + delta + count) % count;
        if (options[index]?.disabled !== true) return index;
      }
      return from;
    },
    [options],
  );

  const choose = useCallback(
    (index: number): void => {
      const option = options[index];
      setOpen(false);
      trigger?.focus();
      if (option === undefined || option.disabled === true) return;
      if (option.value !== value) onChange(option.value);
    },
    [options, value, onChange, trigger],
  );

  const typeAhead = useCallback(
    (key: string): void => {
      const now = Date.now();
      const text = (now - typed.current.at < TYPE_AHEAD_MS ? typed.current.text : '') + key;
      typed.current = { text, at: now };
      const needle = text.toLowerCase();
      const found = options.findIndex(
        (option) => option.disabled !== true && option.label.toLowerCase().startsWith(needle),
      );
      if (found !== -1) setActive(found);
    },
    [options],
  );

  function onKeyDown(event: React.KeyboardEvent<HTMLButtonElement>): void {
    if (disabled) return;

    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        setActive(selectedIndex === -1 ? step(-1, 1) : selectedIndex);
        setOpen(true);
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActive((index) => step(index, 1));
        return;
      case 'ArrowUp':
        event.preventDefault();
        setActive((index) => step(index, -1));
        return;
      case 'Home':
        event.preventDefault();
        setActive(step(-1, 1));
        return;
      case 'End':
        event.preventDefault();
        setActive(step(options.length, -1));
        return;
      case 'Enter':
      case ' ':
        event.preventDefault();
        choose(active);
        return;
      case 'Tab':
        setOpen(false);
        return;
      default:
        break;
    }

    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      typeAhead(event.key);
    }
  }

  useEffect(() => {
    if (!open) return;
    optionNodes.current[active]?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  return (
    <>
      <button
        ref={setTrigger}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open ? optionId(active) : undefined}
        aria-label={props.label}
        disabled={disabled}
        className={props.className === undefined ? 'select' : `select ${props.className}`}
        onClick={() => {
          setActive(selectedIndex === -1 ? step(-1, 1) : selectedIndex);
          setOpen((current) => !current);
        }}
        onKeyDown={onKeyDown}
      >
        <span className={selected === undefined ? 'select-value placeholder' : 'select-value'}>
          {selected?.label ?? props.placeholder ?? ''}
        </span>
        <ChevronDown className="select-chevron" aria-hidden="true" />
      </button>

      {open && trigger !== null && (
        <Popover
          anchor={{ kind: 'element', element: trigger }}
          onClose={(reason) => {
            setOpen(false);
            if (reason === 'escape') trigger.focus();
          }}
        >
          <ul id={listId} role="listbox" aria-label={props.label} className="listbox">
            {options.map((option, index) => (
              <li
                key={option.value}
                id={optionId(index)}
                ref={(node) => {
                  optionNodes.current[index] = node;
                }}
                role="option"
                aria-selected={option.value === value}
                aria-disabled={option.disabled}
                className={listboxOptionClass(index === active, option.value === value)}
                onMouseMove={() => {
                  if (option.disabled !== true) setActive(index);
                }}
                onClick={() => choose(index)}
              >
                <span className="listbox-mark" aria-hidden="true">
                  {option.value === value ? '✓' : ''}
                </span>
                {option.label}
              </li>
            ))}
            {options.length === 0 && <li className="listbox-empty subtle">Nothing to choose.</li>}
          </ul>
        </Popover>
      )}
    </>
  );
}

function listboxOptionClass(isActive: boolean, isSelected: boolean): string {
  return `listbox-option${isActive ? ' active' : ''}${isSelected ? ' selected' : ''}`;
}
